import { EApplicantType } from '@if/core/types'
import { Grid, useTheme } from '@mui/material'
import { getApiClient } from '@src/services/api-client'
import { reportErrorToConsole } from '@src/services/error-logger'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import allApis from '../../../data/api'
import { useAppSelector } from '../../../data/store'
import { creditSelectors } from '../../../data/store/CreditApplication'
import { CreditApplication } from '../../../data/types'
import { CreditReport } from '../../../data/types/CreditReportSchema'
import ApplicantSummary from './applicantSummary'
import CreditReportComponent from './creditReport'
import IncomesTable from './incomesTable'

interface Props {
  applicantCreditReport: CreditReport | null
  setApplicantCreditReport: (creditReport: CreditReport) => void
  coApplicantCreditReport: CreditReport | null
  setCoApplicantCreditReport: (creditReport: CreditReport) => void
  creditApplication: CreditApplication
  selectedTabIndex: number
  refreshReport: (applicant: 'applicant' | 'coApplicant') => void
  userCanRefreshCreditReport: boolean
  editDisabled: boolean
}

const apiClient = getApiClient()

const ApplicantInformations = ({
  creditApplication,
  applicantCreditReport,
  setApplicantCreditReport,
  coApplicantCreditReport,
  setCoApplicantCreditReport,
  refreshReport,
  selectedTabIndex,
  userCanRefreshCreditReport,
  editDisabled,
}: Props) => {
  const navigate = useNavigate()
  const selectedTab: EApplicantType = selectedTabIndex === 2 ? EApplicantType.CoApplicant : EApplicantType.Applicant
  const selectedApplicant =
    selectedTab === EApplicantType.Applicant ? creditApplication?.applicant : creditApplication?.coApplicant
  const externalReportedIncomesForApplicant = useAppSelector(creditSelectors.getFlinksIncomesForApplicant)
  const externalReportedIncomesForCoApplicant = useAppSelector(creditSelectors.getFlinksIncomesForCoApplicant)
  const shouldFetchApplicantCreditReport = selectedTab === EApplicantType.Applicant && !applicantCreditReport
  const shouldFetchCoApplicantCreditReport = selectedTab === EApplicantType.CoApplicant && !coApplicantCreditReport

  const decision =
    selectedTab === EApplicantType.Applicant
      ? (creditApplication.applicant.creditDecision?.decision ??
        creditApplication.applicant?.prequalificationDecision?.decision)
      : (creditApplication.coApplicant?.creditDecision?.decision ??
        creditApplication.coApplicant?.prequalificationDecision?.decision)

  const handleSelectedTab = (index: number) => {
    const { location } = window
    const updateSearch = new URLSearchParams(location.search)
    updateSearch.set('tabIndex', encodeURIComponent(index))
    const newUrl = `${location.pathname}?${updateSearch.toString()}`
    navigate(newUrl, { replace: true })
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (
          creditApplication &&
          apiClient &&
          selectedApplicant?.hardHitReport?.creditReportId &&
          (shouldFetchApplicantCreditReport || shouldFetchCoApplicantCreditReport)
        ) {
          const currentSelectedApplicantCreditReport = await allApis.credit.getCreditReportByCreditReportId(apiClient, {
            financingProgramId: creditApplication.financingProgramId,
            creditApplicationId: creditApplication.id,
            creditReportId: selectedApplicant.hardHitReport.creditReportId,
          })
          if (selectedTab === EApplicantType.Applicant) {
            setApplicantCreditReport(currentSelectedApplicantCreditReport.creditReport)
          }
          if (selectedTab === EApplicantType.CoApplicant) {
            setCoApplicantCreditReport(currentSelectedApplicantCreditReport.creditReport)
          }
        }
      } catch (fetchingError) {
        console.error('Error fetching credit reports:', fetchingError)
      }
    }

    fetchData().catch(reportErrorToConsole)
  }, [
    creditApplication,
    selectedApplicant,
    selectedTab,
    setApplicantCreditReport,
    setCoApplicantCreditReport,
    shouldFetchApplicantCreditReport,
    shouldFetchCoApplicantCreditReport,
  ])

  const theme = useTheme()

  const incomes = (name?: EApplicantType) => {
    return name === EApplicantType.Applicant
      ? creditApplication.applicant.currentJobs
      : creditApplication.coApplicant!.currentJobs
  }

  const otherIncomes = (name?: EApplicantType) => {
    return name === EApplicantType.Applicant
      ? creditApplication.applicant.otherIncomes
      : creditApplication.coApplicant!.otherIncomes
  }

  const externalReportedIncome = (name?: EApplicantType) => {
    return name === EApplicantType.Applicant
      ? externalReportedIncomesForApplicant
      : externalReportedIncomesForCoApplicant
  }

  const previousJobs = (name?: EApplicantType) => {
    return name === EApplicantType.Applicant
      ? creditApplication.applicant.previousJobs
      : creditApplication.coApplicant!.previousJobs
  }

  const shoulDisplayIncome = useAppSelector(creditSelectors.getFinancingProgramConfigs).useIncomeSection

  return (
    <>
      <Grid container marginBottom={2}>
        <Grid
          border="1px solid"
          borderColor={theme.palette.divider}
          borderRadius={2}
          sx={[
            selectedTab === EApplicantType.Applicant
              ? {
                  backgroundColor: theme.palette.action.selected,
                }
              : {
                  backgroundColor: '',
                },
          ]}
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <ApplicantSummary
            applicant={creditApplication.applicant}
            creditApplicationId={creditApplication.id}
            financingProgramId={creditApplication.financingProgramId}
            onClickExpand={() => handleSelectedTab(1)}
            editDisabled={editDisabled}
          />
        </Grid>
        {creditApplication.coApplicant && (
          <Grid
            border="1px solid"
            borderColor={theme.palette.divider}
            borderRadius={2}
            sx={[
              selectedTab === EApplicantType.CoApplicant
                ? {
                    backgroundColor: theme.palette.action.selected,
                  }
                : {
                    backgroundColor: '',
                  },
            ]}
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <ApplicantSummary
              applicant={creditApplication.coApplicant}
              isCoapplicant
              creditApplicationId={creditApplication.id}
              financingProgramId={creditApplication.financingProgramId}
              onClickExpand={() => handleSelectedTab(2)}
              editDisabled={editDisabled}
            />
          </Grid>
        )}
      </Grid>
      {shoulDisplayIncome && (
          <IncomesTable
            incomes={incomes(selectedTab)}
            otherIncomes={otherIncomes(selectedTab)}
            externalReportedIncome={externalReportedIncome(selectedTab)?.annualAverageGrossIncome}
          />
      )}
      {decision && (
        <CreditReportComponent
          refreshReport={() => refreshReport(selectedTab)}
          creditReport={selectedTab === EApplicantType.Applicant ? applicantCreditReport : coApplicantCreditReport}
          softHitReport={
            selectedTab === EApplicantType.Applicant
              ? creditApplication.applicant.softHitReport
              : creditApplication.coApplicant?.softHitReport
          }
          decision={decision}
          userCanRefreshCreditReport={userCanRefreshCreditReport}
          financingProgramId={creditApplication.financingProgramId}
          editDisabled={editDisabled}
          existingOnGoingLoans={
            selectedTab === EApplicantType.Applicant
              ? creditApplication.applicant.existingCustomerReference?.existingOnGoingLoans
              : creditApplication.coApplicant?.existingCustomerReference?.existingOnGoingLoans
          }
          previousJobs={previousJobs(selectedTab)}
        />
      )}
    </>
  )
}

export default ApplicantInformations